import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { map, scan, shareReplay, withLatestFrom } from "rxjs/operators";

import { SearchProductsQuery } from "../common/gql/graphql";

export type FacetValueIdPair = `${string}-${string}`;

export interface FacetWithValues {
    id: string;
    name: string;
    isActive: boolean;
    values: Array<{
        id: string;
        name: string;
        count: number;
        isActive: boolean;
    }>;
}

type FacetValue = SearchProductsQuery['search']['facetValues'][number];

@Injectable({
    providedIn: 'root',
})
export class FacetFilterService {
    facetsWithValues$: Observable<FacetWithValues[]>;
    activeFacetValueIds$ = new BehaviorSubject<FacetValueIdPair[]>([]);
    groupedActiveFacetValueIds$: Observable<string[][]>;

    private allCollectionFacetValues: FacetValue[] | undefined = undefined;
    private facetsValues$ = new Subject<FacetValue[]>();
    private totalResults$ = new BehaviorSubject<number>(0);

    get activeFacetValueIds(): FacetValueIdPair[] {
        return this.activeFacetValueIds$.value;
    }

    constructor() {
        this.facetsWithValues$ = this.facetsValues$.pipe(
            withLatestFrom(this.activeFacetValueIds$, this.totalResults$),
            scan((result, [facetValues, activeFacetValueIds, totalResults]) => {
                if (this.allCollectionFacetValues == null) {
                    this.allCollectionFacetValues = facetValues;
                }
                return this.groupFacetValues(
                    totalResults,
                    facetValues,
                    this.allCollectionFacetValues,
                    activeFacetValueIds,
                    result,
                );
            }, [] as FacetWithValues[]),
            shareReplay(1),
        );

        this.groupedActiveFacetValueIds$ = this.activeFacetValueIds$.pipe(
            map((pairStrings) => {
                const pairs = pairStrings.map((pair) => pair.split('-'));
                const groups: { [facetId: string]: string[] } = {};
                for (const pair of pairs) {
                    const facetId = pair[0];
                    if (!groups[facetId]) {
                        groups[facetId] = [];
                    }
                    groups[facetId].push(pair[1]);
                }
                return Object.values(groups);
            }),
            shareReplay(1),
        );
    }

    init() {
        this.allCollectionFacetValues = undefined;
    }

    setFacetValues(totalResults: number, values: FacetValue[]) {
        this.facetsValues$.next(values);
        this.totalResults$.next(totalResults);
    }

    setActiveFacetValueIds(ids: FacetValueIdPair[]) {
        this.activeFacetValueIds$.next(ids);
    }

    private groupFacetValues(
        totalResults: number,
        facetValues: FacetValue[],
        initialFacetValues: FacetValue[],
        activeFacetValueIdPairs: FacetValueIdPair[],
        lastResult: FacetWithValues[],
    ): FacetWithValues[] {
        const facetMap = new Map<string, FacetWithValues>();
        const activeFacetIds = Array.from(new Set(activeFacetValueIdPairs.map((pair) => pair.split('-')[0])));
        const activeFacetValueIds = activeFacetValueIdPairs.map((pair) => pair.split('-')[1]);
        for (const {
            count,
            facetValue: { id, name, facet },
        } of initialFacetValues) {
            const facetValueIsActive = activeFacetValueIds.includes(id);
            const facetIsActive = activeFacetIds.includes(facet.id);
            const match = facetValues.find((fv) => fv.facetValue.id === id);
            const actualCount = facetIsActive
                ? lastResult.find((f) => f.id === facet.id)?.values.find((v) => v.id === id)?.count ??
                  match?.count ??
                  0
                : match?.count ?? 0;
            const facetFromMap = facetMap.get(facet.id);
            if (facetFromMap) {
                facetFromMap.values.push({ id, name, count: actualCount, isActive: facetValueIsActive });
                if (facetValueIsActive) {
                    facetFromMap.isActive = true;
                }
            } else {
                facetMap.set(facet.id, {
                    id: facet.id,
                    name: facet.name,
                    isActive: facetValueIsActive,
                    values: [{ id, name, count: actualCount, isActive: facetValueIsActive }],
                });
            }
        }
        return Array.from(facetMap.values())
            .filter((facet) => 0 < facet.values.length)
            .map((facet) => {
                facet.values.sort((a, b) => (a.name < b.name ? -1 : 1));
                return facet;
            })
            .sort((a, b) => {
                return a.name < b.name ? -1 : 1;
            });
    }
}
